import * as _ from 'lodash';

import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CacheService } from '../global/ng2-cache/services/cache.service';

import {UtilService} from '../services/util.service';
import { SessionService } from '../services/session2.service';
import { AutomateJobService } from '../services/automate-job.service';
import { AutomateJob, AutomateTask } from '../global/models';
import { GlobalMessageService } from '../services/global-message.service';
import { ConfirmDialogComponent } from '../global/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from '../global/alert-dialog/alert-dialog.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Constants, SkillType, EmailType } from '../global/constants';

@Component({
  selector: 'automate',
  templateUrl: './automate.component.html'
})
export class AutomateComponent implements OnInit {
  loadingPage: boolean = true;
  labs: boolean = false;
  session: any;
  jobs: AutomateJob[] = [];

  searchText: string = ''; // free form search box

  skillTypes: any[] = [];
  selectedSkillTypes: any[] = [];
  skillTypeDDSettings: any = {
    text: 'All Skill Types',
    enableSearchFilter: true,
    badgeShowLimit: 0,
    classes: ''
  };

  emailTypes: any[] = [];
  selectedEmailTypes: any[] = [];
  emailTypeDDSettings: any = {
    text: 'All Email Types',
    enableSearchFilter: false,
    badgeShowLimit: 0,
    classes: ''
  };

  constructor(
    private sessionService: SessionService,
    private constants: Constants,
    private router: Router,
    private cacheService: CacheService,
    private modalService: BsModalService,
    private automateJobService: AutomateJobService,
    private globalMessageService: GlobalMessageService,
    private utilService: UtilService
  ) { }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      );
    }
    else {
      this.initView();
    }
  }

  private initView() {
    this.loadingPage = true;

    // make sure inbox filters exist for this user
    if (!this.session.automate_filters || !this.session.automate_filters.last_changed) {
      this.resetFilters();
    }

    var now = new Date();
    var lastUpdate = new Date(this.session.automate_filters.last_changed);
    if (now.getTime() - lastUpdate.getTime() > 900000) {
      // 900000ms = 15 minutes. Let's reset filters if it's been more than 15 minutes since they were last last changed
      this.resetFilters();
    }

    this.constants.skillTypes.forEach(t => {
      this.skillTypes.push({ id: t.id, itemName: t.name });
    });

    // #hack - remove bot skill from dropdown if the account doesn't have the feature
    if (!this.session.f.bot) {
      this.skillTypes.splice(this.skillTypes.findIndex(st => { return st.id == 'botevent'}));
    }

    if (this.session.automate_filters.skills.length > 0) {
      this.selectedSkillTypes = JSON.parse(JSON.stringify(this.session.automate_filters.skills));
    }

    this.emailTypes = _.map(this.constants.emailTypes, (t: EmailType) => {
      return { id: t.typeName, itemName: t.display };
    });
    this.emailTypes.splice(this.emailTypes.findIndex((t) => { return t.id == 'all'; }), 1);
    this.emailTypes.splice(this.emailTypes.findIndex((t) => { return t.id == 'notification'; }), 1);

    // hide direct types if the feauture is not turned on
    if (!this.session.f.directmail) {
      this.emailTypes.splice(this.emailTypes.findIndex((t) => { return t.id=='humandirect' }), 1);
      this.emailTypes.splice(this.emailTypes.findIndex((t) => { return t.id=='spam' }), 1);
    }

    if (this.session.f) {
      this.labs = this.session.f.labs || false;
    }

    this.emailTypes.sort((a, b) => {
      if (a.itemName > b.itemName) {
        return 1;
      }
      else {
        return -1;
      }
    });

    if (this.session.automate_filters.types.length > 0) {
      this.selectedEmailTypes = JSON.parse(JSON.stringify(this.session.automate_filters.types));
    };

    if (this.session.automate_filters.search.length > 0) {
      this.searchText = this.session.automate_filters.search;
    }

    this.automateJobService.getJobs('all').subscribe(
      (result: any) => {
        this.jobs = result;
        this.jobs.forEach(j => {
          j = this.prettyJob(j);
        });
        this.sortJobs();
        this.filterJobs();
        this.loadingPage = false;
      },
      error => {

      }
    );
  }

  private clearSelectedSkillTypes() {
    this.selectedSkillTypes = [];
    this.filterJobs();
  }

  private clearSelectedEmailTypes() {
    this.selectedEmailTypes = [];
    this.filterJobs();
  }

  private sortJobs() {
    this.jobs.sort((a, b) => {
      if (a.type == 'cdo') {
        return 1;
      }
      else if (a.type == 'enrich') {
        return 1;
      }
      else {
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
        else {
          return -1;
        }
      }
    });
  }

  private filterJobs() {
    this.jobs.forEach((job: any) => {
      job.visible = this.matchesSelectedEmailTypes(job) && this.matchesSelectedSkillTypes(job) && this.matchesSearchFilter(job);
    });
    this.saveSession();
  }

  private matchesSelectedSkillTypes(job: AutomateJob) {
    if (this.selectedSkillTypes.length > 0) {
      let skillMatch = _.find(this.selectedSkillTypes, st => st.id == job.type);
      if (!skillMatch) {
        return false;
      }
    }

    return true;
  }

  private matchesSelectedEmailTypes(job: AutomateJob) {
    if (this.selectedEmailTypes.length > 0) {
      let typesToMatch = this.selectedEmailTypes.map(i => i.id);
      let emailTypeFilter = _.find(job.filters, f => f.type == 'email_type');
      if (emailTypeFilter) {
        let emailTypeMatch = _.intersectionWith(typesToMatch, emailTypeFilter.include, _.isEqual);
        if (emailTypeMatch.length == 0) {
          return false;
        }
      }
    }
    return true;
  }

  private matchesSearchFilter(job: AutomateJob) {
    if (this.searchText.length == 0) {
      return true;
    }
    else {
      let searchTextLower = this.searchText.toLocaleLowerCase(); // so we don't have to keep doing it
      if (job.name.toLowerCase().indexOf(searchTextLower) > -1) {
        return true;
      }

      for (let i = 0; i < job.tasks.length; i++) {
        let task: AutomateTask = job.tasks[i];
        if (this.searchObj(task, searchTextLower)) {
          return true;
        }
        else {
          continue;
        }
      }
    }
    return false;
  }

  /**
   * Helper to search the job for matching
   * @param obj
   * @param query
   */
  private searchObj(obj, query) {
    for (let key in obj) {
      let value = obj[key];

      if (typeof value == 'object') {
        if (this.searchObj(value, query)) {
          return true;
        }
        else {
          continue;
        }
      }
      if (typeof value == 'string') {
        if (value.toLowerCase().indexOf(query) > -1) {
          return true;
        }
        else {
          continue;
        }
      }
    }
    return false;
  }

  /**
   * Create meta data for displaying the list control
   */
  private prettyJob(job: any) {
    job.visible = true;
    job.pretty = {
      name: job.name,
      invalid: false,
      issues: []
    };

    job.tasks.forEach((task: AutomateTask) => {
      if (task.invalid_reason && task.invalid_reason.length > 0) {
        job.pretty.issues.push(task.invalid_reason);
      }
    });
    job.pretty.invalid = job.pretty.issues.length > 0 ? true : false;

    if (job.type == 'cdo') {
      if (job.tasks[0].settings.type == 'recipient') {
        job.pretty.name += ' - Sender Contacts';
      }
      else {
        job.pretty.name += ' - Mined Contacts';
      }
    }

    return job;
  }

  toggle(job: any) {
    if (job.type != 'enrich') {
      job.enabled = !job.enabled;
      job.track_changes = true;
      let tempJob = JSON.parse(JSON.stringify(job));
      this.saveJob(tempJob, `Job was ${job.enabled ? 'enabled' : 'disabled'}`);
    }
    else {
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          header: 'The Enrich Skill is a little different...',
          message: `The on/off switch works a little different on this. To turn off the Enrich skill, you'll have to edit the settings for phone numbers and job titles.`,
          confirmBtn: 'Ok'
        }
      }));
      this.router.navigate(['/config/enrich']);
    }
  }

  private saveJob(job: any, message: string) {
    // clean up the two properties we don't want saved
    delete job.pretty;
    delete job.visible;

    this.automateJobService.saveJob(job).subscribe(
      (result: any) => {
        this.globalMessageService.message(message);
      },
      error => {
        // TODO
      }
    );
  }

  cloneJob(job: any) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Please confirm you\'d like to create a copy of "${job.name}". The new skill will be disabled until you enable it.`,
        yesBtn: 'Clone Skill',
        confirm: () => {
          let newJob = JSON.parse(JSON.stringify(job));
          delete newJob.pretty;
          newJob._id = 'new';
          newJob.name += ' (CLONE)';
          newJob.enabled = false;
          newJob.track_changes = false;

          newJob.tasks.forEach((task: any) => {
            task.taskId = me.utilService.randString(24);
          });

          // can't call this.saveJob because we need to get the result and push into the array
          me.automateJobService.saveJob(newJob).subscribe(
            (nj: any) => {
              nj = me.prettyJob(nj);
              me.jobs.push(nj);
              me.globalMessageService.message(`Skill was cloned to ${nj.name}`);
            },
            error => {
              // TODO
            }
          );
        }
      }
    }));
  }

  deleteJob(job: any) {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `WARNING: All history and associated data for this skill will be removed. Are you sure you want to delete?`,
        yesBtn: 'Delete Skill',
        confirm: function () {
          // first, remove the job from the array so the UI doesn't blow up when we drop meta data
          me.jobs.splice(me.jobs.findIndex(j => j._id == job._id), 1);

          job.enabled = false;
          job.deleted = true;

          job.tasks.forEach(task => {
            task.enabled = false;
            task.deleted = true;
            task.updated_at = new Date();
          });

          me.saveJob(job, 'Skill was deleted');
        }
      }
    }));
  }

  private saveSession() {
    this.session.automate_filters.last_changed = new Date();
    this.session.automate_filters.skills = this.selectedSkillTypes;
    this.session.automate_filters.types = this.selectedEmailTypes;
    this.session.automate_filters.search = this.searchText;
    this.sessionService.saveSession(this.session).subscribe(
      (result: any) => {
        // Nothing
      },
      err => {

      }
    )
  }

  private resetFilters() {
    this.session.automate_filters = {
      skills: [],
      types: [],
      search: '',
    };
    this.saveSession();
  }
}



// WEBPACK FOOTER //
// ./client/app/automate/automate.component.ts